import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './HeroSelection.css';

const HeroSelection = () => {
    const navigate = useNavigate();
    const [userInput, setUserInput] = useState('');

    // Hero workout presets based on character abilities
    const heroWorkouts = [
        { code: "w@d^_~!", name: "Spider Swing Core Power Workout" },
        { code: "$x&*w@d!", name: "Super Soldier Complete Training" },
        { code: "w@~^x&*!", name: "Berserker Rage Unleashed Workout" },
        { code: "w@$x&*~^!", name: "Maximum Effort Chaos Training" },
        { code: "$x&*+=!", name: "Arc Reactor Precision Workout" },
        { code: "$x&*=!", name: "God of Thunder Divine Power" },
        { code: "^w@d_~", name: "Web Slinger Agility Training" },
        { code: "$&x*w@", name: "Shield Bearer Elite Workout" },
        { code: "x&*$w@~", name: "Weapon X Military Protocol" },
        { code: "^~w@$x&*", name: "Fourth Wall Breaking Fitness" },
        { code: "&*$x+=", name: "Repulsor Tech Integration" },
        { code: "&$x*=", name: "Mjolnir Worthiness Training" },
        { code: "@w^d_!", name: "Wall Crawler Ultimate" },
        { code: "w@$&x*", name: "Brooklyn Brawler Conditioning" },
        { code: "~w@^x*", name: "Lone Wolf Survival Training" },
        { code: "$*&x@w~^", name: "Mercenary Combat Conditioning" },
        { code: "*&$x+=!", name: "Genius Billionaire Protocol" },
        { code: "x$&*=+", name: "Bifrost Cosmic Strength" },
        { code: "w@d^_~!", name: "Friendly Neighborhood Hero" },
        { code: "$x&*w@d^!", name: "First Avenger Leadership" },
        { code: "w@~^x&", name: "Healing Factor Recovery" },
        { code: "x&*$w@!", name: "Regenerative Madness Method" },
        { code: "$x&*=+", name: "Armored Avenger Protocol" },
        { code: "$&x*+=!", name: "Asgardian Royal Guard" }
    ];

    // Muscle groups mapping (same as everything system)
    const muscleGroups = [
        { name: "legband", code: "l" }, { name: "chestband", code: "c" }, { name: "backband", code: "b" },
        { name: "coreband", code: "o" }, { name: "armband", code: "r" }, { name: "shoulderband", code: "s" },
        { name: "chest", code: "e" }, { name: "back", code: "k" }, { name: "shoulder", code: "h" },
        { name: "arm", code: "m" }, { name: "bicep", code: "i" }, { name: "tricep", code: "t" },
        { name: "forearm", code: "f" }, { name: "legs", code: "g" }, { name: "abs", code: "a" },
        { name: "calves", code: "v" }, { name: "trap", code: "p" }, { name: "quad", code: "q" },
        { name: "hamstring", code: "n" }, { name: "glute", code: "u" }, { name: "hip", code: "j" },
        { name: "thigh", code: "y" }, { name: "oblique", code: "z" }, { name: "overall", code: "!" },
        { name: "chestgym", code: "$" }, { name: "backgym", code: "x" }, { name: "shouldergym", code: "&" },
        { name: "armgym", code: "*" }, { name: "bicepgym", code: "(" }, { name: "tricepgym", code: ")" },
        { name: "forearmgym", code: "-" }, { name: "abgym", code: "+" }, { name: "leggym", code: "=" },
        { name: "quadgym", code: "[" }, { name: "hamgym", code: "]" }, { name: "glutegym", code: "{" },
        { name: "calvegym", code: "}" }, { name: "trapgym", code: ":" }, { name: "obliquegym", code: ";" },
        { name: "hipgym", code: "|" }, { name: "thighgym", code: "<" }, { name: "overallgym", code: ">" },
        { name: "legbody", code: "~" }, { name: "chestbody", code: "w" }, { name: "backbody", code: "@" },
        { name: "corebody", code: "d" }, { name: "armbody", code: "^" }, { name: "shoulderbody", code: "_" }
    ];

    const allowedChars = "lcborsekhitfgavpqnujyz!$x&*()-+=[]{}:;|<>~w@d^_";

    // Handle adding character to input
    const addCharacter = (char) => {
        const newInput = userInput + char;
        const lastThreeChars = newInput.slice(-3);

        // Prevent three consecutive same characters
        if (lastThreeChars.length < 3 || !(lastThreeChars[0] === lastThreeChars[1] && lastThreeChars[1] === lastThreeChars[2])) {
            setUserInput(newInput);
        }
    };

    // Handle input change
    const handleInputChange = (e) => {
        const input = e.target.value.toLowerCase();
        const validInput = input.split('').filter(char => allowedChars.includes(char)).join('');

        // Check for three consecutive same characters
        let result = '';
        for (let i = 0; i < validInput.length; i++) {
            const char = validInput[i];
            const lastThreeChars = result.slice(-2) + char;
            if (lastThreeChars.length < 3 || !(lastThreeChars[0] === lastThreeChars[1] && lastThreeChars[1] === lastThreeChars[2])) {
                result += char;
            }
        }

        setUserInput(result);
    };

    // Handle preset workout selection
    const handlePresetSelect = (workoutCode) => {
        setUserInput(workoutCode);
    };

    // Handle next button
    const handleNext = () => {
        if (userInput.trim() !== '') {
            const encodedWorkoutCode = encodeURIComponent(userInput);
            navigate(`/hero-rep-count/${encodedWorkoutCode}`, {
                state: {
                    workoutCode: userInput,
                    workoutType: 'hero'
                }
            });
        }
    };

    // Generate hero-themed background
    const getHeroBackground = () => {
        const randomIndex = Math.floor(Math.random() * 88) + 1;
        return `url(/assets/images/v${randomIndex}.jpg)`;
    };

    // Three row character display component
    const ThreeRowCharacterGrid = () => {
        const chunkSize = Math.ceil(allowedChars.length / 3);
        const rows = [];
        for (let i = 0; i < allowedChars.length; i += chunkSize) {
            rows.push(allowedChars.slice(i, i + chunkSize));
        }

        return (
            <div className="three-row-character-grid">
                {rows.map((row, rowIndex) => (
                    <div key={rowIndex} className="character-row">
                        {row.split('').map((char, charIndex) => (
                            <button
                                key={`${rowIndex}-${charIndex}`}
                                className="character-button-row"
                                onClick={() => addCharacter(char)}
                            >
                                {char}
                            </button>
                        ))}
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div
            className="hero-selection-container"
            style={{
                backgroundImage: getHeroBackground(),
                backgroundSize: 'cover',
                backgroundPosition: 'center'
            }}
        >
            {/* Dark overlay */}
            <div className="hero-overlay"></div>

            {/* Fixed top bar */}
            <div className="hero-top-bar">
                <button
                    className="hero-back-button"
                    onClick={() => navigate('/')}
                >
                    ← Back
                </button>

                <input
                    type="text"
                    className="hero-input"
                    value={userInput}
                    onChange={handleInputChange}
                    placeholder="Enter hero code..."
                />

                <button
                    className="hero-next-button"
                    onClick={handleNext}
                    disabled={userInput.trim() === ''}
                >
                    Next ⚡
                </button>
            </div>

            {/* Scrollable content */}
            <div className="hero-content">
                <div className="hero-header">
                    <h1>⚡ HERO TRAINING ⚡</h1>
                    <p>Train like your favorite superheroes!</p>
                </div>

                {/* Muscle group buttons */}
                <div className="muscle-groups-grid">
                    {muscleGroups.map((group) => (
                        <button
                            key={group.code}
                            className="muscle-group-button"
                            onClick={() => addCharacter(group.code)}
                        >
                            <span className="muscle-code">{group.code}</span>
                            <span className="muscle-name">{group.name}</span>
                        </button>
                    ))}
                </div>

                {/* Three-row character selector */}
                <div className="character-selector">
                    <h3>Quick Character Input:</h3>
                    <ThreeRowCharacterGrid />
                </div>

                {/* Preset hero workouts */}
                <div className="preset-workouts">
                    <h3>⚡ Superhero Training Presets ⚡</h3>
                    <div className="preset-workouts-grid">
                        {heroWorkouts.map((workout, index) => (
                            <div
                                key={`${workout.code}-${index}`}
                                className="preset-workout-card hero-preset"
                                onClick={() => handlePresetSelect(workout.code)}
                            >
                                <div className="preset-code">{workout.code}</div>
                                <div className="preset-name">{workout.name}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="hero-instructions">
                    <p>⚡ Train with the power of your favorite heroes!</p>
                    <p>🦸‍♂️ Spider-Man agility, Thor strength, Wolverine endurance</p>
                    <p>🚀 Unlock your inner superhero potential!</p>
                </div>
            </div>
        </div>
    );
};

export default HeroSelection;